import React, {useMemo} from "react";
import {Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import {Delete as DeleteIcon} from "@mui/icons-material";
import ArvoreDeRecursos from "../../Permissions/components/ArvoreDeRecursos";
import {
    buildDireitoDoPlano,
    describeRecurso,
    idsNoDireitoDoPlano,
    NOME_PERFIL_MODELO_MAX,
    normalizePerfisModelo,
    PERFIS_MODELO
} from "../planosComerciaisUtils";

const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

const AvisoForaDoDireito = ({ids, recusadosPelaApi, todos, recursoPorId, onRemover}) => (
    <Alert
        severity={recusadosPelaApi ? 'error' : 'warning'}
        action={<Button color="inherit" size="small" onClick={onRemover}>Tirar do perfil</Button>}
    >
        <Typography variant="body2" fontWeight={700}>
            {recusadosPelaApi
                ? 'A API recusou: fora do direito do plano (módulos incluídos por padrão) ou não existem mais.'
                : `Fora do direito do plano (módulos incluídos por padrão): saem do perfil ao salvar.${todos ? ' O plano fica sem este modelo.' : ''}`}
        </Typography>
        <Box component="ul" sx={{m: 0, pl: 2.5}}>
            {ids.map((id) => (
                <li key={id}><Typography variant="body2">{describeRecurso(recursoPorId.get(id), id)}</Typography></li>
            ))}
        </Box>
    </Alert>
);

const PerfilModeloBloco = ({definicao, perfil, direito, mostrarArvore, carregado, idsNoDireito, recursoPorId, recusados, onChange}) => {
    const {tipo, titulo, semModelo} = definicao;
    const selecionados = perfil.recursos;
    const recusadosPelaApi = (recusados || []).filter((id) => selecionados.includes(id));
    const foraDoDireito = carregado ? selecionados.filter((id) => !idsNoDireito.has(id)) : [];
    const destacados = [...new Set([...recusadosPelaApi, ...foraDoDireito])];

    const atualizar = (updater) => {
        onChange((current) => {
            const perfis = current.perfis_modelo || normalizePerfisModelo(null);
            return {...current, perfis_modelo: {...perfis, [tipo]: updater(perfis[tipo])}};
        });
    };

    const definirRecursos = (fn) => atualizar((atual) => ({...atual, recursos: fn(atual.recursos)}));

    const alternar = (recurso) => definirRecursos((ids) => (
        ids.includes(recurso.id) ? ids.filter((id) => id !== recurso.id) : [...ids, recurso.id]
    ));

    const definirTodos = (recursos, liberar) => definirRecursos((ids) => {
        const alvo = new Set(recursos.map((recurso) => recurso.id));
        return liberar ? [...new Set([...ids, ...alvo])] : ids.filter((id) => !alvo.has(id));
    });

    return (
        <Box
            role="group"
            aria-label={`Perfil ${titulo}`}
            sx={{
                p: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                backgroundColor: 'background.paper',
            }}
        >
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" fontWeight={700}>{titulo}</Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            color={selecionados.length ? 'success' : 'default'}
                            label={selecionados.length ? plural(selecionados.length, 'recurso', 'recursos') : 'Sem modelo'}
                        />
                    </Stack>
                    {selecionados.length ? (
                        <Button color="error" startIcon={<DeleteIcon />} onClick={() => definirRecursos(() => [])}>
                            Limpar
                        </Button>
                    ) : null}
                </Stack>

                <TextField
                    label="Nome do perfil"
                    value={perfil.nome}
                    onChange={(event) => atualizar((atual) => ({...atual, nome: event.target.value}))}
                    inputProps={{maxLength: NOME_PERFIL_MODELO_MAX}}
                    helperText={`Nome do perfil criado em cada licença do plano. Vazio, fica "${definicao.nomePadrao}".`}
                    fullWidth
                />

                {destacados.length ? (
                    <AvisoForaDoDireito
                        ids={destacados}
                        recusadosPelaApi={recusadosPelaApi.length > 0}
                        todos={destacados.length === selecionados.length}
                        recursoPorId={recursoPorId}
                        onRemover={() => definirRecursos((ids) => ids.filter((id) => !destacados.includes(id)))}
                    />
                ) : null}

                {mostrarArvore ? (
                    <ArvoreDeRecursos
                        modulos={direito}
                        isLiberado={(id) => selecionados.includes(id)}
                        onAlternar={alternar}
                        onDefinirTodos={definirTodos}
                        maxHeight={320}
                    />
                ) : null}

                {!selecionados.length ? (
                    <Typography variant="caption" color="text.secondary">{semModelo}</Typography>
                ) : null}
            </Stack>
        </Box>
    );
};

const EstadoDoDireito = ({form, estado, vazio}) => {
    if (!form.id_sistema) {
        return <Typography variant="body2" color="text.secondary">Selecione o sistema do plano para escolher os recursos.</Typography>;
    }
    if (!estado || estado.status === 'carregando') {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Carregando os recursos do sistema...</Typography>
            </Stack>
        );
    }
    if (estado.status === 'erro') {
        return <Alert severity="error">Não foi possível carregar os recursos do sistema. Feche e abra o plano de novo.</Alert>;
    }
    if (vazio) {
        return (
            <Box sx={{p: 3, border: (theme) => `1px dashed ${theme.palette.divider}`, borderRadius: 2, textAlign: 'center'}}>
                <Typography variant="body2" color="text.secondary">
                    Nenhum recurso no direito do plano. Inclua por padrão um módulo com endpoints para montar os perfis.
                </Typography>
            </Box>
        );
    }
    return null;
};

/*
 * Perfis-modelo do plano: Operador e Administrador, cada um com nome e recursos dentro do direito do
 * plano (módulos marcados como incluídos por padrão no formulário, antes mesmo de salvar).
 */
export default function PerfisModeloEditor({form, modulos, recursosPorSistema, perfisRecusados, onChange}) {
    const estado = form.id_sistema ? recursosPorSistema?.[form.id_sistema] : null;
    const carregado = estado?.status === 'pronto';
    const recursos = useMemo(() => (carregado ? estado.itens : []), [carregado, estado]);

    const direito = useMemo(() => buildDireitoDoPlano({form, modulos, recursos}), [form, modulos, recursos]);
    // O mesmo conjunto que o salvamento usa para tirar do modelo o que saiu do direito.
    const idsNoDireito = useMemo(() => idsNoDireitoDoPlano(form, recursos), [form, recursos]);
    const recursoPorId = useMemo(() => new Map(recursos.map((recurso) => [recurso.id, recurso])), [recursos]);
    const mostrarArvore = carregado && direito.length > 0;
    const perfis = form.perfis_modelo || normalizePerfisModelo(null);

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="subtitle1" fontWeight={700}>Perfis de acesso da assinatura</Typography>
                <Typography variant="body2" color="text.secondary">
                    Operador: usuário técnico que portais usam para cadastrar clientes neste plano. Administrador: primeiro administrador do cliente.
                </Typography>
            </Box>

            <EstadoDoDireito form={form} estado={estado} vazio={carregado && direito.length === 0} />

            {PERFIS_MODELO.map((definicao) => (
                <PerfilModeloBloco
                    key={definicao.tipo}
                    definicao={definicao}
                    perfil={perfis[definicao.tipo]}
                    direito={direito}
                    mostrarArvore={mostrarArvore}
                    carregado={carregado}
                    idsNoDireito={idsNoDireito}
                    recursoPorId={recursoPorId}
                    recusados={perfisRecusados?.[definicao.tipo]}
                    onChange={onChange}
                />
            ))}
        </Stack>
    );
}
